import { useState } from "react";
import type { Client } from "../../../types";
import { Button } from "../../../components/ui/button";
import { api } from "../../../api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { toast } from "sonner";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";
import { Trash2 } from "lucide-react";

interface Props {
  client: Client | null;
  onDeleted: () => void;
}

export function DeleteClientButton({ client, onDeleted }: Props) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].deleteDialog;
  const [open, setOpen] = useState(false);
  if (!client) return null;

  const del = async () => {
    const res = await api.deleteClient(client.id);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    onDeleted();
  };

  return (
    <div className="flex justify-center w-full">
      <Button 
        variant="ghost" 
        className="w-full h-12 text-[10px] font-bold uppercase tracking-[0.2em] text-red-400 hover:text-red-600 hover:bg-red-50 rounded-2xl gap-2 transition-all active:scale-95 border border-transparent hover:border-red-100" 
        onClick={() => setOpen(true)}
      >
        <Trash2 className="w-4 h-4" />
        {t.button}
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl">
          <AlertDialogHeader className="text-left p-2">
            <AlertDialogTitle className="text-lg font-bold text-neutral-800 uppercase tracking-widest">
              {t.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-medium text-neutral-500 leading-relaxed pt-2">
              {t.description.replace("{{name}}", client.name)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="p-2 gap-2">
            <AlertDialogCancel className="h-11 rounded-2xl text-[10px] font-bold uppercase tracking-widest border-neutral-100 hover:bg-neutral-50">
              {t.cancel}
            </AlertDialogCancel>
            <AlertDialogAction className="h-11 rounded-2xl text-[10px] font-bold uppercase tracking-widest bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200" onClick={del}>
              {t.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
